import React from 'react'

// Top-level error boundary. Catches render-time exceptions anywhere
// below it in the tree and shows a fallback instead of an empty
// document (which is what React leaves behind when a component
// throws and there's no boundary). Without this, an exception in a
// route or a hook surfaced as "the white screen" the user sees in
// the iOS app switcher.
//
// Localisation: a useTranslation() call inside the boundary would
// itself be liable to throw during early init or after an i18n
// failure — defeating the point. Instead the boundary takes a
// language hint via `dir` and shows hard-coded bilingual strings.
// The reload button works regardless of locale.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // Log to console so the user can grab the stack from devtools /
    // remote-debugger on iOS. Sentry-style remote logging would go
    // here too if we add it later.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, info?.componentStack)

    // Auto-recover from stale-bundle errors. After a Cloudflare deploy
    // pushes a new build, the SW may serve the old index.html that
    // references chunks the new build no longer has. Two shapes:
    //   1. `ChunkLoadError` / "Loading chunk N failed" — the obvious one.
    //   2. `Minified React error #...` — old bundle's React executes
    //      against a tree that doesn't match the new code path, usually
    //      surfacing as hook-order mismatches (#310, #418, #423). The
    //      update toast only appears on the home route, so users deep
    //      inside /news, /social, /tech never see the prompt and hit
    //      this on the next code-split navigation.
    // The session counter caps us at one auto-recover per session, so a
    // genuine bug doesn't loop — it'll show the UI on the second pass.
    const msg = (error?.message || error?.name || '').toString()
    const stack = (error?.stack || '').toString()
    const chunkLikely = /chunk|loading|importmap|module/i.test(msg)
      || /Minified React error/i.test(msg)
      || /Minified React error/i.test(stack)
      || error?.name === 'ChunkLoadError'
    if (!chunkLikely) return
    try {
      const k = 'phoenix:eb-auto-recover'
      const n = Number(sessionStorage.getItem(k) || '0')
      if (n >= 1) return // already tried this session; show the UI
      sessionStorage.setItem(k, String(n + 1))
    } catch { return /* private mode — fall through to UI */ }
    // Run the cache-clear path immediately. The user sees the error
    // screen for a few frames at most before the reload kicks in.
    this.handleClearAndReload()
  }

  handleReload = () => {
    // Hard reload — bypasses cache. If the error came from stale
    // chunks the next load gets fresh bundles.
    try { window.location.reload() } catch { /* noop */ }
  }

  handleClearAndReload = async () => {
    // Aggressive recovery: unregister every SW + clear caches before
    // reloading. Helps when the SW is serving a broken precache.
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map((r) => r.unregister()))
      }
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys()
        await Promise.all(keys.map((k) => caches.delete(k)))
      }
    } catch { /* best effort */ }
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const message = this.state.error?.message || String(this.state.error || 'Unknown error')
    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground"
      >
        <div className="max-w-md w-full text-center space-y-4">
          <div className="text-5xl" aria-hidden="true">⚠️</div>
          <h1 className="text-lg font-semibold" dir="auto">
            حصل خطأ غير متوقع
          </h1>
          <p className="text-xs text-muted-foreground" dir="auto">
            Something went wrong. The app couldn't render — usually a
            stale offline bundle. Try reloading; if that doesn't help,
            clear the cache and reload.
          </p>
          {message && (
            <pre
              className="text-[10px] text-muted-foreground bg-muted rounded p-2 text-start whitespace-pre-wrap break-all max-h-32 overflow-y-auto"
              dir="ltr"
            >
              {message}
            </pre>
          )}
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md bg-app-primary-500 text-white text-sm font-medium hover:opacity-90"
            >
              تحديث / Reload
            </button>
            <button
              type="button"
              onClick={this.handleClearAndReload}
              className="px-4 py-2 rounded-md border border-input text-foreground text-sm font-medium hover:bg-accent"
            >
              مسح الكاش / Clear cache
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default ErrorBoundary
